import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  cloneElement,
} from "react";
import Map, {
  FullscreenControl,
  GeolocateControl,
  ScaleControl,
  NavigationControl,
  Source,
  Layer,
} from "react-map-gl";
import dynamic from "next/dynamic";
import MapMarker from "components/maps/MapMarker/MapMarker";

const ExperiencePopup = dynamic(
  () => import("components/maps/popups/ExperiencePopup/ExperiencePopup")
);
const DestinationPopup = dynamic(
  () => import("components/maps/popups/DestinationPopup/DestinationPopup")
);

const Spiderfier = dynamic(() => import("../Spiderfier/Spiderfier"));

import useWindowSize from "lib/hooks/useWindowSize";
// const useInView = dynamic(() =>
//   import("react-cool-inview").then((mod) => mod.useInView)
// );
import { useInView } from "react-cool-inview";

import { getCountryInfo } from "utils/countries";
import "mapbox-gl/dist/mapbox-gl.css";
import styles from "./DynamicMap.module.scss";
import {
  clusterLayer,
  clusterCountLayer,
  unclusteredPointLayer,
} from "../layers/layers";

const DynamicMap = ({
  lat,
  lng,
  zoom,
  markers,
  showMarkers = true,
  showSpiderfier = true,
  mapSourceID,
  geoJSON,
  children,
  destinationData,
  resize,
  projection = "globe",
}) => {
  // console.log(geoJSON);
  const [viewport, setViewport] = useState({
    latitude: lat,
    longitude: lng,
    zoom: zoom - zoom / 2,
  });
  const [geoMarkers, setGeoMarkers] = useState({});
  const [spiderfiedMarkers, setSpiderfiedMarkers] = useState([]);
  const [singleMarkers, setSingleMarkers] = useState([]);
  const [unclusteredMarkers, setUnclusteredMarkers] = useState([]);
  const [activeSpiderfier, setActiveSpiderfier] = useState(null);
  const [clusters, setClusters] = useState();
  const [currentZoom, setCurrentZoom] = useState();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [activeMarker, setActiveMarker] = useState(null);
  const [popupInfo, setPopupInfo] = useState(null);
  const [hoveredCountryInfo, setHoveredCountryInfo] = useState(null);
  const [selectedCountryInfo, setSelectedCountryInfo] = useState(null);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [isCountryPopupActive, setIsCountryPopupActive] = useState(false);
  const [firstRenderComplete, setFirstRenderComplete] = useState(false);

  const countryLayer = {
    id: "country-boundaries",
    source: {
      type: "vector",
      url: "mapbox://mapbox.country-boundaries-v1",
    },
    filter: [
      "all",
      [
        "any",
        ["==", "all", ["get", "worldview"]],
        ["in", "US", ["get", "worldview"]],
      ],
    ],
    "source-layer": "country_boundaries",
    type: "fill",
    paint: {
      "fill-color": "#000",
      "fill-opacity": 0,
    },
  };

  const countryLayerTransparent = {
    id: "country-boundaries-transparent",
    source: {
      type: "vector",
      url: "mapbox://mapbox.country-boundaries-v1",
    },

    filter: [
      "all",
      [
        "any",
        ["==", "all", ["get", "worldview"]],
        ["in", "US", ["get", "worldview"]],
      ],
    ],
    "source-layer": "country_boundaries",
    type: "fill",
    paint: {
      "fill-color": "hsla(216, 100%, 37%, 1)",
      "fill-opacity": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        0.5,
        0,
      ],
    },
  };

  const { observe, inView } = useInView({
    unobserveOnEnter: true,
  });

  const debounce = function (func, wait, immediate) {
    let timeout;
    return function () {
      const context = this,
        args = arguments;
      const later = function () {
        timeout = null;
        if (!immediate) func.apply(context, args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func.apply(context, args);
    };
  };

  const handleSpiderfierClick = (event, active) => {
    const targetID = event?.target?.id;

    event.stopPropagation();
    event.preventDefault();
    setIsCountryPopupActive(false);
    setActiveSpiderfier(activeSpiderfier === targetID ? null : targetID);
  };

  const handlePopupClose = (event) => {
    setIsCountryPopupActive(false);
    setPopupInfo(null);
    setActiveMarker(null);
  };

  const handleCountryPopupClose = (event) => {
    event?.preventDefault ? event.preventDefault() : "";
    setIsCountryPopupActive(false);
  };

  const handleMarkerClick = useCallback(
    (event, id, feature) => {
      const handler = (event, id) => {
        setActiveMarker((previous) => {
          return id === previous ? null : id;
        });

        event.originalEvent.stopPropagation();
        event.originalEvent.preventDefault();

        const { geometry, properties } = feature;
        const latitude = geometry.coordinates[1],
          longitude = geometry.coordinates[0];

        const { description, link, title, onClose, image, imageTags, name } =
          properties;

        const popupData = {
          latitude,
          longitude,
          title,
          image,
          description,
          link,
          // onClose,
          imageTags,
        };

        setPopupInfo((previous) => {
          return id === activeMarker
            ? null
            : { latitude, longitude, ...properties };
        });
      };

      handler(event, id, feature);
    },
    [activeMarker]
  );

  useEffect(() => {
    setGeoMarkers({});

    if (!geoJSON?.features || !inView) {
      return;
    }

    geoJSON?.features?.forEach((feature) => {
      const spiderProperty = feature?.geometry?.coordinates.join("-");

      setGeoMarkers((previous) => {
        const next = { ...previous };

        if (previous?.features?.find((marker) => marker?.id)) {
          return { ...previous };
        }

        next[spiderProperty] = Array.isArray(next[spiderProperty])
          ? [...next[spiderProperty], feature]
          : [feature];

        return next;
      });
    });
  }, [geoJSON?.features, geoJSON, inView, mapLoaded]);

  useEffect(() => {
    if (!inView || !showMarkers) {
      return;
    }

    setSingleMarkers(() => {
      const markerArray = Object.entries(geoMarkers)
        .filter((entry) => entry[1].length === 1)
        .map((entry, index) => {
          return entry[1]?.map((feature, index) => {
            const markerElement =
              clusters?.length && spiderfiedMarkers ? (
                <MapMarker
                  latitude={feature.geometry.coordinates[1]}
                  longitude={feature.geometry.coordinates[0]}
                  active={activeMarker === feature?.id}
                  onClick={(event) =>
                    handleMarkerClick(event, feature?.id, feature)
                  }
                  itemID={feature?.id}
                  iconName="WhereNextLogo"
                  key={`${feature?.properties?.name}-${feature?.id}-${index}`}
                  visible={unclusteredMarkers?.includes(feature?.id)}
                />
              ) : (
                <MapMarker
                  latitude={feature.geometry.coordinates[1]}
                  longitude={feature.geometry.coordinates[0]}
                  active={activeMarker === feature?.id}
                  onClick={(event) =>
                    handleMarkerClick(event, feature?.id, feature)
                  }
                  itemID={feature?.id}
                  iconName="WhereNextLogo"
                  key={`${feature?.properties?.name}-${feature?.id}-${index}`}
                />
              );

            return markerElement;
          });
        });

      return markerArray;
    });
  }, [
    geoMarkers,
    setSingleMarkers,
    unclusteredMarkers,
    inView,
    spiderfiedMarkers,
    clusters?.length,
    activeSpiderfier,
    activeMarker,
    handleMarkerClick,
    showMarkers,
  ]);

  useEffect(() => {
    if (!inView || !showMarkers || !showSpiderfier) {
      return;
    }

    setSpiderfiedMarkers(() => {
      const spiderClusters = Object.entries(geoMarkers)
        .filter((entry) => entry[1].length > 1)
        .map((entry, index) => {
          const spiderfierLocation = entry[1][0]?.geometry?.coordinates;
          const latitude = spiderfierLocation[1];
          const longitude = spiderfierLocation[0];
          const uniqueID = `${spiderfierLocation}-spiderfier`;
          const entryIDs = entry[1].map((feature) => feature?.id);

          return (
            <Spiderfier
              key={index}
              id={uniqueID}
              latitude={latitude}
              longitude={longitude}
              active={activeSpiderfier === uniqueID}
              clusters={clusters}
              onClick={handleSpiderfierClick}
            >
              {entry[1]?.map((feature, index) => (
                <MapMarker
                  latitude={latitude}
                  longitude={longitude}
                  active={activeMarker === feature?.id}
                  onClick={(event) =>
                    handleMarkerClick(event, feature?.id, feature)
                  }
                  itemID={feature?.id}
                  iconName="WhereNextLogo"
                  key={`${feature?.properties?.name}-${feature?.id}-${index}`}
                />
              ))}
            </Spiderfier>
          );
        });

      return spiderClusters;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    geoMarkers,
    clusters,
    inView,
    activeSpiderfier,
    currentZoom,
    activeMarker,
  ]);

  const { width } = useWindowSize();

  const mapRef = useRef();

  const onMapLoad = (event) => {
    setMapLoaded(true);
    updateMapState(event);
  };

  const onMapClick = (event) => {
    if (!mapRef.current) {
      return;
    }

    event.originalEvent.stopPropagation();

    const mapboxSource = mapRef.current.getSource("dynamic-map-geojson");
    const feature = event.features[0] ?? false;

    setIsCountryPopupActive(() => false);

    if (feature) {
      /* React to to cluster click */
      const clusterId = feature.properties.cluster_id;

      mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) {
          return;
        }

        mapRef.current.easeTo({
          center: feature.geometry.coordinates,
          zoom: zoom > 12 ? 12 : zoom,
          duration: 800,
        });
      });
    }

    const country = mapRef.current.queryRenderedFeatures(event.point, {
      layers: ["country-boundaries-transparent"],
    });
    const sourceFeatures = mapRef.current.querySourceFeatures(
      "country-boundaries-transparent"
    );

    const properties = country.map((object) => object.properties);
    if (!properties.length) {
      return;
    }

    const clickedCountryInfo =
      {
        iso_3166_1: properties[0]?.iso_3166_1,
        name: properties[0]?.name,
        region: properties[0]?.region,
        lngLat: event.lngLat,
      } ?? null;

    const clickedCountry = getCountryInfo(clickedCountryInfo.iso_3166_1);

    if (destinationData) {
      const matchingDestination = destinationData.find(
        (destination) =>
          destination.destination_details.country_short_code ===
          clickedCountry.code
      );

      setSelectedDestination(() => {
        return { ...matchingDestination };
      });

      const selectedCountryPopupInfo = {
        ...clickedCountryInfo,
        ...clickedCountry,
        lngLat: event.lngLat,
        id: matchingDestination.id ?? null,
      };

      if (properties && properties.length) {
        setSelectedCountryInfo(() => {
          return { ...selectedCountryPopupInfo };
        });

        setIsCountryPopupActive(() => true);
      }
    }
  };

  const updateMapState = (event) => {
    if (
      !showMarkers ||
      !mapRef.current ||
      mapRef.current.getLayer("clusters") === undefined
    ) {
      return;
    }

    if (!mapRef.current) {
      return;
    }

    const mapboxSource = mapRef.current.getSource("dynamic-map-geojson");

    const currentClusters = mapRef.current.queryRenderedFeatures({
      layers: ["clusters"],
    });

    setClusters(() => {
      const updatedClusters = currentClusters?.map((cluster) => {
        let clusterLeaves = {};
        let leafIDs = [];

        mapboxSource?.getClusterLeaves(
          cluster?.id,
          100,
          0,
          (error, features) => {
            clusterLeaves[cluster?.id] = features.map((feature) => {
              leafIDs.push(feature?.id);
            });
          }
        );

        return {
          clusterInfo: cluster,
          leaves: clusterLeaves,
          leafIDs: leafIDs,
        };
      });

      return [...updatedClusters];
    });
  };

  const getUnclusteredMarkerFeatures = () => {
    if (!showMarkers) {
      return;
    }

    const unclusteredMarkerFeatures = showMarkers
      ? mapRef.current.querySourceFeatures("dynamic-map-geojson", {
          sourceLayer: "unclustered-point",
        })
      : [];

    const unclusteredMarkerIDs = unclusteredMarkerFeatures.map(
      (marker) => marker?.id
    );
    setUnclusteredMarkers(Array.from(new Set(unclusteredMarkerIDs)));

    return Array.from(new Set(unclusteredMarkerFeatures));
  };

  const onMapZoom = (event) => {
    if (showMarkers) {
      getUnclusteredMarkerFeatures();

      updateMapState(event);
    }
    setCurrentZoom(event.viewState.zoom);
  };

  const onMapMouseMove = debounce((event) => {
    if (!mapRef.current) {
      return;
    }

    const feature = mapRef.current.queryRenderedFeatures(event.point, {
      layers: ["country-boundaries-transparent"],
    });
    const properties = feature.map((object) => object.properties);

    if (!feature.length) {
      mapRef.current.getCanvas().style.cursor = "grab";
    }

    const hoveredCountry =
      {
        iso_3166_1: properties[0]?.iso_3166_1,
        name: properties[0]?.name,
        region: properties[0]?.region,
        lngLat: event.lngLat,
      } ?? null;

    setHoveredCountryInfo((previous) => {
      if (previous?.id) {
        mapRef.current.setFeatureState(
          {
            source: "country-boundaries",
            sourceLayer: "country_boundaries",
            id: previous.id,
          },
          { hover: false }
        );
      }

      if (feature && feature[0]) {
        mapRef.current.setFeatureState(
          {
            source: "country-boundaries",
            sourceLayer: "country_boundaries",
            id: feature[0].id,
          },
          { hover: true }
        );
        mapRef.current.getCanvas().style.cursor = "pointer";
      }

      return {
        ...getCountryInfo(hoveredCountry.iso_3166_1),
        lngLat: event.lngLat,
        id: feature[0]?.id ?? null,
      };
    });
  });

  const onMapMouseLeave = (event) => {
    // console.log(event);
    mapRef.current.setFeatureState(
      {
        source: "country-boundaries",
        sourceLayer: "country_boundaries",
        id: hoveredCountryInfo.id,
      },
      { hover: false }
    );
  };

  useEffect(() => {
    if (mapRef.current && firstRenderComplete && lng && lat) {
      mapRef.current.flyTo({
        center: [lng, lat],
        zoom: zoom / 2,
        speed: 0.5,
        essential: false, // this animation is considered essential with respect to prefers-reduced-motion
      });
    }
    if (width < 992) {
      setIsCountryPopupActive(false);
    }
  }, [lat, lng, zoom, width, setIsCountryPopupActive]);

  useEffect(() => {
    if (mapRef.current) {
      window.setTimeout(() => {
        mapRef.current.resize();
      }, 200);
    }
    if (width < 992) {
      setIsCountryPopupActive(false);
    }
  }, [resize, width, setIsCountryPopupActive]);

  useEffect(() => {
    if (!geoJSON) {
      return;
    }
    const countryCodeList = geoJSON?.features?.map((feature) => {
      if (!feature?.properties?.data?.destination_title) {
        return "none";
      }
      return feature?.properties?.data?.destination_details?.ISO_code;
    });

    // setCountryLayer((previous) => {
    //   let filter = previous.filter;
    //   const countryFilter = filter.slice(0, 2);

    //   countryCodeList.forEach((code) => countryFilter.push(code));

    //   filter = countryFilter;

    //   return { ...previous, filter };
    // });
  }, [geoJSON]);

  useEffect(() => {
    setFirstRenderComplete(true);
  }, []);

  return (
    <>
      <div className={`${styles.mapWrapper}`} ref={observe}>
        {inView && (
          <Map
            initialViewState={viewport}
            projection={projection}
            ref={mapRef}
            onLoad={onMapLoad}
            onClick={onMapClick}
            onZoom={onMapZoom}
            onMouseMove={onMapMouseMove}
            onRender={(event) => event.target.resize()}
            // onMouseOut={onMapMouseLeave}
            interactiveLayerIds={[clusterLayer.id]}
            style={{
              width: "100%",
              height: "100%",
              minHeight: width > 992 ? 450 : 400,
            }}
            mapStyle="mapbox://styles/mapbox/streets-v8?optimize=true"
            // minZoom={2}
            maxZoom={16}
            fog={{
              range: [0.8, 4],
              color: "hsla(0, 0%, 100%, 1)",
              "horizon-blend": 0.5,
              "high-color": "#245bde",
              "space-color": "#000000",
              "star-intensity": 0.5,
            }}
            mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN}
          >
            {showMarkers && (
              <>
                {spiderfiedMarkers && showSpiderfier && spiderfiedMarkers}
                {singleMarkers && !activeSpiderfier && singleMarkers}
                {markers && markers}
              </>
            )}
            <Source
              id="country-boundaries"
              type="vector"
              url="mapbox://mapbox.country-boundaries-v1"
              generateId={true}
              // promoteId="mapbox_id"
            >
              <Layer {...countryLayer} />
              <Layer {...countryLayerTransparent} />
            </Source>

            <Source
              id="dynamic-map-geojson"
              type="geojson"
              data={geoJSON}
              cluster={showMarkers}
              clusterMaxZoom={18}
              clusterRadius={10}
            >
              <>
                <Layer {...clusterLayer} />
                <Layer {...clusterCountLayer} />
              </>

              {showMarkers && <Layer {...unclusteredPointLayer} />}
            </Source>
            <GeolocateControl position="top-right" />
            <FullscreenControl position="top-right" />
            {/* <ScaleControl position="top-right" /> */}
            <NavigationControl position="top-right" />

            {!!popupInfo && activeMarker && (
              <>
                {popupInfo.type === "experience" && (
                  <ExperiencePopup
                    popupInfo={popupInfo}
                    onClose={handlePopupClose}
                  />
                )}
                {popupInfo.type === "destination" && (
                  <DestinationPopup
                    popupInfo={popupInfo}
                    onClose={handlePopupClose}
                    closeOnClick={false}
                  />
                )}
              </>
            )}

            {children}

            {isCountryPopupActive &&
              selectedCountryInfo &&
              selectedDestination && (
                <div className={`${styles.countryPopupWrapper}`}>
                  <DestinationPopup
                    popupInfo={{
                      latitude: selectedCountryInfo.lngLat.lat,
                      longitude: selectedCountryInfo.lngLat.lng,
                      data: selectedDestination,
                    }}
                    onClose={handleCountryPopupClose}
                    closeOnClick={false}
                  />
                </div>
              )}
          </Map>
        )}
      </div>
    </>
  );
};

export default React.memo(DynamicMap);
